/**
 * StripeConnectOnboarding — Embedded ConnectAccountOnboarding for activation.
 *
 * Reuses the dashboard session. New setup shows preparation; saved setup resumes directly.
 */

import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { ConnectAccountOnboarding } from '@stripe/react-connect-js';
import { Building2, UserRound, Landmark, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { getAuthToken } from '../../../utils/auth';
import { useStripeSession } from '../StripeSessionContext';
import StripeEmbedBoundary from '../StripeEmbedBoundary';
import { STRIPE_ACTIVATION_COLLECTION_OPTIONS } from '../stripeConnectCollectionOptions';
import StripeSuccessScreen from './StripeSuccessScreen';
import { getStripeExitState } from '../stripeSetupState';
import { useOnboardingDriver } from '../GuidedFormCreation/OnboardingDriverContext';
import { accountsClient } from '../../../lib/trpc/accounts';
import { STRIPE_INFO_PARAGRAPHS } from './stripeActivationPrefaceCopy';
import { safeCapture } from '../../../utils/analytics';

const POLL_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 20_000;

/** Error source tags — used to show the right recovery message. */
const ERR_SOURCE = {
  ACCOUNT_SESSION: 'account_session',  // POST /stripe/account-session failed
  CONNECT_INIT: 'connect_init',         // loadConnectAndInitialize threw (ad blocker, CSP, bad key)
  EMBED_LOAD: 'embed_load',             // ConnectAccountOnboarding onLoadError (iframe/session failure)
};

/** StepIndicator progress while Connect Stripe step is active (activation bar). */
export const STRIPE_ACTIVATION_PROGRESS = {
  prepare: 50,
  embed: 60,
  success: 70,
};

export default function StripeConnectOnboarding({ merchant, onComplete, onClose, onActivationProgressPercentChange, resumeSetup = false }) {
  const posthog = usePostHog();
  const onboardingCtx = useOnboardingDriver();
  const refreshPaymentStatus = onboardingCtx?.refreshPaymentStatus;
  const formConfig = onboardingCtx?.formConfig;
  const displayFundraiserName = merchant?.merchantName || merchant?.organizationName || '';
  const formIdForAnalytics =
    onboardingCtx?.publishedFormId || merchant?.lastPublishedFormId;
  const orgIdForAnalytics = merchant?.organizationId || merchant?.id;

  const session = useStripeSession();
  const connectInstance = session?.instance;
  const [initError, setInitError] = useState(null);
  const [exitState, setExitState] = useState(null);
  const retryKey = session?.generation || 0;
  const [loaderStarted, setLoaderStarted] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const mountedRef = useRef(true);
  const exitRequestRef = useRef(0);
  const statusControllers = useRef(new Set());
  const exitCountRef = useRef(0);

  const [userClickedContinue, setUserClickedContinue] = useState(resumeSetup);
  const prefaceAnalyticsSent = useRef(false);
  const startedAnalyticsScope = useRef(null);

  const pollTimerRef = useRef(null);
  const pollDeadlineRef = useRef(null);
  const posthogRef = useRef(posthog);
  const merchantForAnalyticsRef = useRef(merchant);
  const formIdForAnalyticsRef = useRef(formIdForAnalytics);

  posthogRef.current = posthog;
  merchantForAnalyticsRef.current = merchant;
  formIdForAnalyticsRef.current = formIdForAnalytics;

  useEffect(() => {
    if (resumeSetup || userClickedContinue || initError || session?.error || !session || prefaceAnalyticsSent.current) return;
    prefaceAnalyticsSent.current = true;
    safeCapture(posthog, 'onboarding_stripe_preface_shown', {
      org_id: orgIdForAnalytics,
      form_id: formIdForAnalytics,
      form_name: formConfig?.formName,
      display_name: displayFundraiserName || undefined,
      form_type: formConfig?.formType,
      phase: 'activation',
    });
  }, [orgIdForAnalytics, formIdForAnalytics, formConfig?.formName, formConfig?.formType, displayFundraiserName, posthog, resumeSetup, userClickedContinue, initError, session]);

  useEffect(() => {
    const analyticsScope = `${session?.scope}:${retryKey}`;
    if (!userClickedContinue || !connectInstance || startedAnalyticsScope.current === analyticsScope) return;
    startedAnalyticsScope.current = analyticsScope;
    safeCapture(posthogRef.current, 'onboarding_stripe_connect_started', {
      org_id: merchantForAnalyticsRef.current?.organizationId || merchantForAnalyticsRef.current?.id,
      form_id: formIdForAnalyticsRef.current,
      phase: 'activation', retry_attempt: retryKey,
    });
  }, [userClickedContinue, connectInstance, session?.scope, retryKey]);

  useEffect(() => {
    setInitError(null);
    setLoaderStarted(false);
  }, [retryKey, session?.scope]);

  useEffect(() => {
    if (!userClickedContinue || !connectInstance || loaderStarted) return;
    const timer = setTimeout(() => setInitError(new Error('Stripe is taking longer than expected to load. Please try again.')), 25000);
    return () => clearTimeout(timer);
  }, [userClickedContinue, connectInstance, loaderStarted]);

  useEffect(() => {
    const refresh = () => refreshPaymentStatus?.();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [refreshPaymentStatus]);

  const checkStatus = useCallback(async ({ live = false } = {}) => {
    const controller = new AbortController();
    statusControllers.current.add(controller);
    const timeout = setTimeout(() => controller.abort(), 8000);
    const aborted = new Promise((_, reject) => controller.signal.addEventListener('abort', () => reject(new DOMException('Cancelled', 'AbortError')), { once: true }));
    try {
      const token = await Promise.race([getAuthToken(), aborted]);
      if (controller.signal.aborted) return null;
      const resp = await Promise.race([
        accountsClient.request('GET /stripe/account-status', {
          authorization: token,
          input: live ? { live: true } : {},
          signal: controller.signal,
        }),
        aborted,
      ]);
      if (!resp.ok) {
        const errText = await resp.text().catch(() => '');
        console.error(`[StripeConnect] checkStatus failed: ${resp.status} ${errText}`);
        return null;
      }
      return await Promise.race([resp.json(), aborted]);
    } catch (err) {
      console.error('[StripeConnect] checkStatus exception:', err);
      Sentry.captureException(err, {
        tags: {
          area: 'onboarding',
          operation: 'stripe_account_status',
        },
        extra: {
          orgId:
            merchantForAnalyticsRef.current?.organizationId ||
            merchantForAnalyticsRef.current?.id,
          formId: formIdForAnalyticsRef.current,
        },
      });
      return null;
    } finally {
      clearTimeout(timeout);
      statusControllers.current.delete(controller);
    }
  }, []);

  const startPollingLoop = useCallback(() => {
    setExitState('polling');
    const generation = ++exitRequestRef.current;
    pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
    const poll = async () => {
      if (!mountedRef.current || generation !== exitRequestRef.current) return;
      if (Date.now() >= pollDeadlineRef.current) {
        setExitState('unavailable');
        refreshPaymentStatus?.();
        return;
      }
      const status = await checkStatus({ live: true });
      if (!mountedRef.current || generation !== exitRequestRef.current) return;
      const result = getStripeExitState(status);
      if (result === 'checking') {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } else {
        refreshPaymentStatus?.();
        if (result === 'incomplete') setExitState('incomplete');
        else { setExitState(null); setShowSuccessScreen(true); }
      }
    };
    pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
  }, [checkStatus, refreshPaymentStatus]);

  const handleExit = useCallback(async () => {
    exitCountRef.current += 1;
    const exitNum = exitCountRef.current;
    const request = ++exitRequestRef.current;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    const status = await checkStatus({ live: true });
    if (!mountedRef.current || request !== exitRequestRef.current) return;
    refreshPaymentStatus?.();

    const result = getStripeExitState(status);
    if (result === 'submitted' || result === 'approved') safeCapture(posthogRef.current, 'onboarding_stripe_connect_submitted', {
      org_id:
        merchantForAnalyticsRef.current?.organizationId ||
        merchantForAnalyticsRef.current?.id,
      form_id: formIdForAnalyticsRef.current,
      phase: 'activation',
      details_submitted: status?.detailsSubmitted ?? null,
      charges_enabled: status?.chargesEnabled ?? null,
      exit_number: exitNum,
    });


    if (result === 'checking') {
      startPollingLoop();
    } else if (result === 'incomplete') {
      setExitState('incomplete');
    } else {
      setExitState(null);
      setShowSuccessScreen(true);
    }
  }, [checkStatus, startPollingLoop, refreshPaymentStatus]);

  const handleSuccessScreenComplete = useCallback(() => {
    setShowSuccessScreen(false);
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    mountedRef.current = true;
    const controllers = statusControllers.current;
    return () => {
      mountedRef.current = false;
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const showCelebrationPreface = !userClickedContinue;
  const showStripeEmbedCard = connectInstance && userClickedContinue;
  const connectionError = initError || session?.error;

  const activationProgressPercent = useMemo(() => {
    if (showSuccessScreen) return STRIPE_ACTIVATION_PROGRESS.success;
    if (connectionError) return STRIPE_ACTIVATION_PROGRESS.prepare;
    if (exitState === 'incomplete' || exitState === 'polling') return STRIPE_ACTIVATION_PROGRESS.embed;
    if (showStripeEmbedCard) return STRIPE_ACTIVATION_PROGRESS.embed;
    return STRIPE_ACTIVATION_PROGRESS.prepare;
  }, [showSuccessScreen, connectionError, exitState, showStripeEmbedCard]);

  useEffect(() => {
    onActivationProgressPercentChange?.(activationProgressPercent);
  }, [activationProgressPercent, onActivationProgressPercentChange]);

  if (showSuccessScreen) {
    return <StripeSuccessScreen paymentsEnabled={onboardingCtx?.chargesEnabled === true} stripeApproved={onboardingCtx?.confirmationPending === true} reviewing={onboardingCtx?.reviewPending === true} onContinue={handleSuccessScreenComplete} />;
  }

  if (connectionError || !session) {
    return <div role="alert" className="rounded-md border border-edge bg-paper-light p-5 text-center">
      <p className="text-sm font-medium text-ink">Could not load Stripe setup</p>
      <p className="mt-2 text-sm text-ink-secondary">{connectionError?.message || 'The secure Stripe session is unavailable. Reopen setup to try again.'}</p>
      <p className="mt-2 text-xs leading-relaxed text-ink-secondary">Your saved Stripe information is kept. If Stripe opens a separate sign-in window, complete it there and return to this page.</p>
      <Button variant="primary" size="workspace" className="mt-4" onClick={() => { setInitError(null); refreshPaymentStatus?.(); session?.retry(); }}>Try again</Button>
    </div>;
  }

  if (exitState === 'unavailable') {
    return <div className="rounded-md border border-edge bg-paper-light p-5 text-center"><p className="text-sm font-medium text-ink">We could not confirm your Stripe status</p><p className="mt-2 text-sm text-ink-secondary">Your information may have been saved. Close this window while we keep checking, or return to Stripe to review your setup.</p><Button variant="neutral" size="workspace" className="mt-4" onClick={() => setExitState(null)}>Return to Stripe setup</Button></div>;
  }

  if (exitState === 'incomplete') {
    return (
      <div className="rounded-md border border-[color:var(--cs-status-warning-border)] bg-status-amber-bg p-5 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-paper-light rounded-md mb-4">
          <svg className="w-6 h-6 text-status-amber-text" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <p className="text-status-amber-text font-semibold mb-2">Please complete all fields to continue</p>
        <p className="text-ink-secondary text-sm mb-6">
          {onboardingCtx?.chargesEnabled ? 'Payments remain enabled, but Stripe needs more information for your account.' : 'Stripe has not confirmed that your account can accept donations yet.'} You can close setup and return later.
        </p>
        <button
          onClick={() => setExitState(null)}
          className="bg-sage hover:bg-sage-hover text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
        >
          Return to Stripe Setup
        </button>
      </div>
    );
  }

  if (exitState === 'polling') {
    return (
      <div className="rounded-md border border-edge bg-paper-light p-5 text-center">
        <div className="inline-block w-6 h-6 border-2 border-sage border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-ink font-medium mb-1">Checking your Stripe status…</p>
        <p className="text-ink-secondary text-sm">Checking whether Stripe needs more information. You can close this window anytime.</p>
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      {showCelebrationPreface && (
        <motion.div
          key="preface"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="w-full"
        >
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-medium text-ink">What you’ll need</h3>
              <p className="mt-1 text-xs leading-relaxed text-ink-secondary">Stripe will ask for a few details to verify your nonprofit and send donations to your bank.</p>
            </div>
            <div className="divide-y divide-edge rounded-md border border-edge bg-paper-light">
              {[
                { icon: Building2, title: 'Organization details', description: 'Your nonprofit’s legal name, address, and tax information.' },
                { icon: UserRound, title: 'Your information', description: 'Contact and identity details for your organization’s representative.' },
                { icon: Landmark, title: 'Bank account', description: 'The account where you want to receive your donations.' },
              ].map(({ icon: Icon, title, description }) => <div key={title} className="flex items-start gap-3 px-4 py-3"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" /><div><p className="text-sm text-ink">{title}</p><p className="mt-0.5 text-xs leading-relaxed text-ink-secondary">{description}</p></div></div>)}
            </div>
            <p className="text-xs leading-relaxed text-ink-secondary">Stripe may open a secure sign-in window. Complete it there, then return here to finish setup. Publishing and sharing unlock once payment activation is confirmed.</p>
            <Popover>
              <PopoverTrigger asChild><Button variant="ghost" size="sm" className="gap-2 text-ink-secondary"><QuestionMarkCircleIcon className="h-4 w-4 shrink-0" />Why we use Stripe</Button></PopoverTrigger>
              <PopoverContent side="top" align="start" className="max-h-[min(420px,70vh)] w-[min(22rem,calc(100vw-2rem))] overflow-y-auto border-edge bg-paper-light p-4 shadow-paper">
                <p className="mb-2 text-sm font-medium text-ink">Stripe &amp; your payouts</p>
                <div className="space-y-3 text-xs leading-relaxed text-ink-secondary">{STRIPE_INFO_PARAGRAPHS.map((paragraph, i) => <p key={i}>{paragraph}</p>)}</div>
              </PopoverContent>
            </Popover>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-edge pt-4">
              {onClose && <Button variant="neutral" size="workspace" onClick={onClose}>Set up later</Button>}
              <Button variant="primary" size="workspace" className="gap-2" onClick={() => { onboardingCtx?.markPaymentSetupStarted?.(); setUserClickedContinue(true); }} disabled={!connectInstance}>
                {connectInstance ? <>Continue to Stripe<ArrowRight className="h-4 w-4 shrink-0" /></> : <><span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />Preparing Stripe…</>}
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      {userClickedContinue && !connectInstance && <div role="status" className="rounded-md border border-edge bg-paper-light p-5 text-sm text-ink-secondary">Loading your saved Stripe setup…</div>}

      {showStripeEmbedCard && (
        <motion.div
          key="embed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35 }}
          className="w-full"
        >
          <div className="flex flex-col gap-4">
            <div className="min-h-[200px]">
              {!loaderStarted && <p role="status" className="mb-3 text-sm text-ink-secondary">Loading Stripe setup…</p>}
              <StripeEmbedBoundary key={`${session.scope}:${retryKey}`} onRetry={session.retry}>
                <ConnectAccountOnboarding
                  key={`${session.scope}:${retryKey}`}
                  onLoaderStart={() => setLoaderStarted(true)}
                  collectionOptions={STRIPE_ACTIVATION_COLLECTION_OPTIONS}
                  onExit={handleExit}
                  onLoadError={(err) => {
                    console.error('[StripeConnect] embed load error:', err);
                    // Tag as embed load failure (expired/invalid client secret, iframe blocked)
                    if (err && typeof err === 'object') {
                      err._stripeErrorSource = ERR_SOURCE.EMBED_LOAD;
                    }
                    safeCapture(posthogRef.current, 'onboarding_stripe_connect_failed', {
                      org_id:
                        merchantForAnalyticsRef.current?.organizationId ||
                        merchantForAnalyticsRef.current?.id,
                      form_id: formIdForAnalyticsRef.current,
                      phase: 'activation',
                      error_source: ERR_SOURCE.EMBED_LOAD,
                      retry_attempt: retryKey,
                    });
                    Sentry.captureException(err, {
                      tags: {
                        area: 'onboarding',
                        operation: 'stripe_connect_embed_load',
                      },
                      extra: {
                        orgId:
                          merchantForAnalyticsRef.current?.organizationId ||
                          merchantForAnalyticsRef.current?.id,
                        formId: formIdForAnalyticsRef.current,
                        retryAttempt: retryKey,
                      },
                    });
                    setInitError(new Error('Stripe could not display your setup. Try again to create a fresh secure session.'));
                  }}
                />
              </StripeEmbedBoundary>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-1.5 border-t border-edge pt-4">
                <p className="text-xs text-ink-muted">Stripe may open a secure sign-in window. You can close setup and return anytime.</p>
                <Button variant="ghost" size="sm" className="text-xs text-ink-secondary" onClick={() => { setInitError(null); setLoaderStarted(false); session.retry(); }}>Reload Stripe</Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex rounded-full p-1 text-sage transition-colors hover:bg-sage-light hover:text-sage-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
                      aria-label="More about Stripe and secure payouts"
                    >
                      <QuestionMarkCircleIcon className="h-4 w-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="top"
                    align="center"
                    sideOffset={8}
                    className="max-h-[min(420px,70vh)] w-[min(22rem,calc(100vw-2rem))] overflow-y-auto border-edge bg-paper-light p-4 text-left font-inter shadow-paper z-50"
                  >
                    <p className="mb-2 text-xs font-semibold text-ink">Stripe &amp; your payouts</p>
                    <div className="space-y-2 text-xs leading-relaxed text-ink-secondary">
                      {STRIPE_INFO_PARAGRAPHS.map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
            </div>

          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
